import { FC, useMemo, useState } from 'react';
import { hideLoadingModalReducer, setAlertResponse, setValuesAndOpenAlertModalReducer, showLoadingModalReducer } from 'src/store';
import { FloatingSaveBar, Hr } from 'src/components/_commons';
import { months } from '@monorepo/helpers';
import { PublicMinistryByCalendar } from './PublicMinistryByCalendar';
import { PublicMinistryByDate } from './PublicMinistryByDate';
import { PublicMinistryByType } from './PublicMinistryByType';
import { PublicMinistryCrudAddButton } from './PublicMinistryCrudAddButton';
import { PublicMinistryCrudServiceModal } from './PublicMinistryCrudServiceModal';
import { PublicMinistryRadio, typeOption } from './PublicMinistryRadio';
import { publicMinistryServices } from 'src/services';
import { typePublicMinistryDoc, typePublicMinistryMonthlyService, typePublicMinistryService, typeUser } from '@monorepo/models';
import { useDispatch } from 'react-redux';
import { useGetIsDarkMode, useGetShowingLoadingModal } from 'src/custom-hooks';

type propsType = {
    closeCrud: (update: boolean) => void;
    month: number;
    monthlyServiceToEdit?: typePublicMinistryMonthlyService | null;
    publicMinistryDoc: typePublicMinistryDoc;
    users: typeUser[];
    year: number;
};

export const PublicMinistryCrud: FC<propsType> = ({
    closeCrud, month, monthlyServiceToEdit, publicMinistryDoc, users, year
}) => {
    const [monthlyService, setMonthlyService] = useState<typePublicMinistryMonthlyService>(monthlyServiceToEdit ?? { month, services: [], year });
    const [radioValue, setRadioValue] = useState<typeOption>('byType');
    const [showAdd, setShowAdd] = useState(false);
    const dispatch = useDispatch();
    const isDarkMode = useGetIsDarkMode();
    const isEdit = !!monthlyServiceToEdit;
    const showingLoadingModal = useGetShowingLoadingModal();

    const isValid: boolean = useMemo(() =>
        !!monthlyService.services.length
    , [monthlyService]);

    const saveHandler = () => {
        dispatch(setValuesAndOpenAlertModalReducer({
            title: 'Confirmar',
            message: `Se va a guardar el estado actual del Servicio del Campo para ${months[month].toUpperCase()} de ${year}`,
            mode: 'confirm',
            execution: async () => {
                dispatch(showLoadingModalReducer());
                const response = await publicMinistryServices.Save({
                    month,
                    services: monthlyService.services.filter(s => !s.isOtherMonth),
                    year
                });
                dispatch(hideLoadingModalReducer());
                if (!response.success) {
                    dispatch(setAlertResponse({ response }));
                    return;
                }
                closeCrud(true);
            }
        }));
    }

    const cancelHandler = () => {
        dispatch(setValuesAndOpenAlertModalReducer({
            title: '¿Cancelar?',
            message: 'Se van a perder todos los cambios hechos hasta acá',
            mode: 'confirm',
            animation: 3,
            execution: () => closeCrud(true)
        }));
    }

    const deleteHandler = () => {
        dispatch(setValuesAndOpenAlertModalReducer({
            title: 'Confirmar',
            message: `Se va a eliminar el Servicio del Campo para ${months[month].toUpperCase()} de ${year}. Esta acción es irreversible.`,
            animation: 3,
            mode: 'confirm',
            execution: async () => {
                dispatch(showLoadingModalReducer());
                const response = await publicMinistryServices.Delete(monthlyService);
                dispatch(hideLoadingModalReducer());
                if (!response.success) {
                    dispatch(setAlertResponse({ response }));
                    return;
                }
                closeCrud(true);
            }
        }));
    }

    return (
        <div className={isDarkMode ? 'text-white' : ''}>
            {showAdd &&
                <PublicMinistryCrudServiceModal
                    addService={(newService: typePublicMinistryService) => {
                        setMonthlyService(x => ({
                            ...x,
                            services: [...x.services, newService]
                        }));
                    }}
                    closeModal={() => setShowAdd(false)}
                    monthlyService={monthlyService}
                    users={users}
                />
            }
            <PublicMinistryCrudAddButton
                showAdd={() => setShowAdd(true)}
            />
            <h2 className={'text-center'}>
                {months[month].toUpperCase()} {year}
            </h2>
            <PublicMinistryRadio
                radioValue={radioValue}
                setRadioValue={setRadioValue}
            />
            {radioValue === 'byType' ?
                <PublicMinistryByType
                    monthlyService={monthlyService}
                    publicMinistryDoc={publicMinistryDoc}
                    setMonthlyService={setMonthlyService}
                    users={users}
                />
                :
                radioValue === 'byDate' ?
                    <PublicMinistryByDate
                        monthlyService={monthlyService}
                    />
                    :
                    <PublicMinistryByCalendar
                        monthlyService={monthlyService}
                    />
            }
            <FloatingSaveBar
                cancelHandler={cancelHandler}
                isDisabledSaveButton={!isValid}
                saveHandler={saveHandler}
            />
            {isEdit &&
                <>
                    <Hr classes={'my-5'} />

                    <div className={'d-flex justify-content-center gap-2 pb-5 mt-5'}>
                        <button className={'btn btn-yellow border text-white'}
                            style={{ width: '150px' }}
                            onClick={deleteHandler}
                            disabled={showingLoadingModal}
                        >
                            Eliminar
                        </button>
                    </div>
                </>
            }
        </div>
    );
}
